/**
 * Horizontal strip of location-pinned pills shown above the time grid in
 * week/day views. Reads the same task list the sidebar does, filters to
 * tasks with a place_id and a due date within the visible lookahead, and
 * surfaces them as drag sources. Dropping a pill on a time slot creates a
 * commitment event; the pill remains because the geofence stays armed.
 *
 * Month view uses `LocationLaneBadge` instead — a compact pin + count
 * decoration per cell since there's no room for a full strip.
 */

import React from 'react'
import { MapPin } from 'lucide-react'
import type { Task } from '../../services/taskService'
import { LocationPill } from './LocationPill'

export interface OpportunisticLaneTask {
  task: Task
  placeTitle: string | null
}

interface OpportunisticLaneProps {
  tasks: OpportunisticLaneTask[]
  onOpenTask?: (taskId: number) => void
  /** Short label shown on the left of the strip */
  label?: string
}

export const OpportunisticLane: React.FC<OpportunisticLaneProps> = ({
  tasks,
  onOpenTask,
  label = 'Location',
}) => {
  if (tasks.length === 0) return null
  return (
    <div className="flex items-center gap-2 px-2 py-1 border-b border-soft bg-brand-subtle/40 min-h-8 overflow-x-auto">
      <span className="flex items-center gap-1 text-app-micro font-medium text-brand-primary flex-shrink-0 uppercase tracking-wide">
        <MapPin className="h-3 w-3" />
        {label}
      </span>
      <div className="flex items-center gap-1.5 overflow-x-auto">
        {tasks.map(({ task, placeTitle }) => (
          <LocationPill
            key={task.id}
            task={task}
            placeTitle={placeTitle}
            onOpen={onOpenTask}
          />
        ))}
      </div>
    </div>
  )
}

interface LocationLaneBadgeProps {
  count: number
}

export const LocationLaneBadge: React.FC<LocationLaneBadgeProps> = ({ count }) => {
  if (count <= 0) return null
  return (
    <span
      title={`${count} location-tied task${count === 1 ? '' : 's'} tied here`}
      className="inline-flex items-center gap-0.5 px-1 py-0 rounded-full text-app-nano font-medium text-brand-primary bg-brand-subtle border border-brand-subtle-border"
    >
      <MapPin className="h-2.5 w-2.5" />
      {count}
    </span>
  )
}

export default OpportunisticLane
